// Components==============
import Button from 'components/UI/Button/Button';
import DreamSelectModal from 'components/UI/DreamSelectModal/DreamSelectModal';
import ProgressGraph from 'components/UI/ProgressGraph/ProgressGraph';
import SkillModal from 'components/UI/SkillModal/SkillModal';
import Slider from 'components/UI/Slider/Slider';
import SparkGoals from 'components/UI/SparkGoals/SparkGoals';
import SparkLinkModal from 'components/UI/SparkLinkModal/SparkLinkModal';
import { AnimatePresence, motion } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import { ReflectContext } from 'pages/Growth/Reflect/Reflect';
import EmojiSelector from 'pages/Growth/Reflect/components/ReflectionItem/components/EmojiSelector/EmojiSelector';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { framerFade } from 'utilities/FramerAnimations';
import { FilteredActivities } from 'utilities/getFilterActivities';
import { ReflectionItemContext } from '../../ReflectionItem';
import ReflectionTextarea from '../../ReflectionTextarea/ReflectionTextarea';
import ReflectionOverviewArea from '../ReflectionOverviewArea/ReflectionOverviewArea';
import './EndPointReflection.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';
// =========================

const TextArea = ({
  note,
  index,
}: {
  note: ReflectionNoteEntity;
  index: number;
}) => {
  const { saveState, save, setSaveState } = useContext(ReflectionItemContext);

  const organisationQuestions = useRedux(
    (state) => state.authentication?.user.organisation.metadata?.questions
  );
  const [value, setValue] = useState(saveState.note?.[index]?.answer || '');

  function handleSave() {
    const saveValues = () => {
      if (!saveState.note?.length)
        return {
          ...saveState,
          note: organisationQuestions?.map((q, i) =>
            i === index ? { ...q, answer: value } : q
          ),
        };

      return {
        ...saveState,
        note: saveState.note.map((n, i) =>
          i === index ? { ...n, answer: value } : n
        ),
      };
    };

    setSaveState(saveValues());
    save(saveValues());
  }

  return (
    <ReflectionTextarea
      item={note}
      value={value}
      setValue={setValue}
      handleSave={handleSave}
    />
  );
};

export default function EndPointReflection({
  completed,
}: {
  completed: boolean;
}) {
  const {
    reflection,
    saveState,
    setSaveState,
    activity,
    groupedSparks,
  } = useContext(ReflectionItemContext);
  const { endPointActivities, project, handleContributeAllDreams } = useContext(
    ReflectContext
  );
  const { loading } = useContext(ReflectContext);

  const [sparkLinkModalIsOpen, setSparkLinkModalIsOpen] = useState(false);

  const organisationQuestions = useRedux(
    (state) => state.authentication?.user.organisation.metadata?.questions
  );

  const peerReflections = useRedux((state) => {
    const reflectionsPeers = state.reflection?.entities.allIds.map(
      (id) => state.reflection?.entities.byId[id]
    );

    return reflectionsPeers?.filter(
      (r) =>
        r?.project_process_id === activity?.id &&
        r?.for_user?.id === state.authentication?.user?.id
    );
  });

  const graphData = useRedux((state) => {
    const reflectionsData = state.reflection?.entities.allIds.map(
      (id) => state.reflection?.entities.byId[id]
    );

    return endPointActivities
      .map(
        (e) =>
          reflectionsData?.find(
            (r) =>
              r?.project_process_id === e?.id &&
              r?.user.id === state.authentication?.user?.id
          ) || 'filler'
      )
      .filter((e) => e);
  });

  const history = useHistory();

  const [value, setValue] = useState(reflection?.result || 0);
  const translate = useTranslate();

  useEffect(() => {
    if (value) setSaveState((prev) => ({ ...prev, result: value }));
  }, [value]);

  const [skillModalIsOpen, setSkillModalIsOpen] = useState(false);
  const [sparkName, setSparkName] = useState<SparkEntity>();

  const [allSelectedActivities, setAllSelectedActivities] = useState<
    FilteredActivities[]
  >([]);

  // const sparks = Object.values(groupedSparks).flatMap((skill) => skill.sparks);

  const getSparkName = (id: number) => {
    const spark = project?.sparks?.find((s) => s.id === id);
    setSparkName(spark);
  };

  return (
    <>
      <motion.div styleName="container" {...framerFade}>
        {!!project && (
          <div>
            <ProgressGraph
              data={graphData}
              project={project}
              topLeft={
                <div style={{ display: 'flex', gap: '0.25em' }}>
                  <strong>{translate('reflection.terms.progress')}:</strong>
                  <p>{translate('reflection.experience-vs-result')}</p>
                </div>
              }
            />
          </div>
        )}
        <div styleName={`wrapper ${loading ? 'loading' : ''}`}>
          <div>
            <h3>
              {translate('reflection.terms.experience')}
              {loading && (
                <FontAwesomeIcon
                  styleName="loading-icon"
                  icon={faSpinnerThird}
                  spin
                />
              )}
            </h3>
            <span>{translate('reflection.experience-activity-end')}</span>
            <EmojiSelector />
          </div>
          <div>
            <h3>{translate('general.terms.result')}</h3>
            <span>{translate('reflection.result-activity-end')}</span>
            <Slider value={value} setValue={setValue} />
          </div>
          <div>
            <h3> {translate('general.terms.spark-goals')} </h3>
            <div>
              {Object.keys(groupedSparks!).map((skillId) => (
                <SparkGoals
                  type="reflection"
                  key={skillId}
                  sparks={groupedSparks![skillId].sparks}
                  setSparkLinkModalIsOpen={setSparkLinkModalIsOpen}
                  getSparkName={getSparkName}
                />
              ))}
              {/* <div>
                <Button
                  color={sparks.length ? 'purple' : 'double-grey'}
                  onClick={() => setSkillModalIsOpen(true)}
                >
                  {sparks.length ? `${sparks.length} ` : ''}
                  Spark
                  {sparks.length > 1 || sparks.length === 0 ? 's' : ''}{' '}
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </div> */}
            </div>
          </div>
          <div />
          {!!organisationQuestions?.length &&
            organisationQuestions.map((note, i) => (
              <TextArea note={note} key={i} index={i} />
            ))}
          <div styleName="button-wrapper">
            <DreamSelectModal
              selected={saveState.dreams}
              handleSubmit={(dreams) => handleContributeAllDreams(dreams)}
            />
            <Button
              color={completed ? 'green' : 'grey'}
              inactive={!completed}
              onClick={() => history.push('/groei/mijn-reflecties')}
            >
              {translate(
                `reflection.${
                  completed ? 'end-reflection-done' : 'not-reflected'
                }`
              )}
            </Button>
          </div>
        </div>
        {!!peerReflections && (
          <ReflectionOverviewArea
            inactive={!peerReflections?.length}
            reflections={peerReflections}
            endPoint
          />
        )}
      </motion.div>
      <AnimatePresence>
        {!!skillModalIsOpen && (
          <SkillModal setSkillModalIsOpen={setSkillModalIsOpen} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {!!sparkLinkModalIsOpen && (
          <SparkLinkModal
            allSelectedActivities={allSelectedActivities}
            setAllSelectedActivities={setAllSelectedActivities}
            setSparkLinkModalIsOpen={setSparkLinkModalIsOpen}
            sparkName={sparkName}
          />
        )}
      </AnimatePresence>
    </>
  );
}
